import { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Form } from 'react-router-dom';

import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';

import CustomSelect from '../../../../../../../common/CustomSelect';
import CustomButton from '../../../../../../../common/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import { useUpdateUserAdditionalInfoMutation } from '../../../../../../../store/services/customizeOptionsApi';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';
import CustomAutoSelect from '../../../../../../../common/CustomAutoSelect';
import { useGetLocationsQuery } from '../../../../../../../store/services/authApi';

import styles from './CityAndAiroportForm.module.scss';

const locationFieldAutocompleteSlotProps = {
  popper: {
    placement: 'bottom-start' as const,
    modifiers: [{ name: 'flip' as const, enabled: false }],
    sx: (theme: Theme) => ({
      zIndex: theme.zIndex.modal + 2,
    }),
  },
};

type FormikValues = {
  city?: string;
  airport?: string;
  units?: string;
};

type CityAndAiroportFormProps = {
  closeModal: () => void;
};

const CityAndAiroportForm: FC<CityAndAiroportFormProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);

  const [cityInput, setCityInput] = useState('');
  const [airportInput, setAirportInput] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [airportSearch, setAirportSearch] = useState('');

  const { data: cities, isFetching: isFetchingCities } = useGetLocationsQuery({ search: citySearch, type: 'city' });
  const { data: airports, isFetching: isFetchingAirports } = useGetLocationsQuery({
    search: airportSearch,
    type: 'airport',
  });
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCitySearch(cityInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [cityInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAirportSearch(airportInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [airportInput]);

  const initialValues = {
    city: user.nearest_city,
    airport: user.nearest_airport,
    units: user.unit_system === 'Metric' ? 'Km/°C' : 'Miles/°F',
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      nearest_city: values.city,
      nearest_airport: values.airport,
      unit_system: values.units === 'Miles/°F' ? 'Imperial' : 'Metric',
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error('Error updating user info:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  };

  return (
    <Box className={styles.cityAndAiroport}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
          <Form
            className={styles.cityAndAiroportForm}
            onSubmit={handleSubmit}
          >
            <Typography variant="h5Bold">Let's personalize your AI assistant</Typography>
            <Typography variant="h10">Just a few quick answers to give you the best experience</Typography>

            <CustomAutoSelect
              options={cities ?? []}
              inputLabel="Nearest City"
              placeholder="Enter city"
              handleChange={(e, value) => {
                setFieldValue('city', value ? value.name : '');
              }}
              onInputChange={(event, inputValue) => {
                setCityInput(inputValue);
              }}
              value={values.city ? { name: values.city } : null}
              loading={isFetchingCities}
              slotProps={locationFieldAutocompleteSlotProps}
            />

            <CustomAutoSelect
              options={airports ?? []}
              inputLabel="Nearest Airport"
              placeholder="Enter airport"
              handleChange={(e, value) => setFieldValue('airport', value ? value.name : '')}
              onInputChange={(event, inputValue) => {
                setAirportInput(inputValue);
              }}
              value={values.airport ? { name: values.airport } : null}
              loading={isFetchingAirports}
              slotProps={locationFieldAutocompleteSlotProps}
            />

            <CustomSelect
              name="units"
              inputLabel="Units of Measure"
              handleChange={handleChange}
              required={true}
              value={values.units}
              data={['Miles/°F', 'Km/°C']}
            />

            <Box className={styles.additionalInfo}>
              <Typography
                variant="h11"
                className={styles.additionalInfoText}
              >
                To get badges, fill in all the questions
              </Typography>
              <img
                src={InformationIcon}
                className={styles.additionalInfoImg}
                alt="info"
              />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType="button"
                variant="outlined"
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType="submit"
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Nearest City and Airport updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CityAndAiroportForm;
